// Case-insensitive file-name patterns: dotted suffixes (foo.test.ts), per-ecosystem
// underscore suffixes, pytest naming (test_foo.py, conftest.py), Jest snapshots, and tsd
// type tests (index.test-d.ts). Underscore suffixes are limited to the exact pairings each
// ecosystem enforces (Go _test.go, pytest *_test.py, RSpec _spec.rb / minitest _test.rb,
// ExUnit _test.exs, Dart _test.dart) so prod modules like ab_test.ts and non-conventions
// like _spec.go or _test.ex don't match. Spec suffixes are handled with spec directories
// below.
const TEST_FILE_NAME_PATTERN =
  /\.(?:tests?|stories|cy|e2e|e2e-spec)\.\w+$|\.test-d\.(?:ts|tsx|mts|cts)$|_test\.(?:go|py|rb|dart)$|_spec\.rb$|_test\.exs$|(?:^|\/)test_[^/]+\.py$|(?:^|\/)conftest\.py$|\.snap$/i;

// Case-insensitive test-only directories: Jest sentinels (__tests__/, __mocks__/), generic
// names (test/, testdata/, cypress/, e2e/), end-to-end suites (e2e-tests/, playwright/),
// shared test helpers (test-utils/, testutil/, testkit/), and .NET test projects
// (Billing.Tests/, Billing.IntegrationTests/).
const TEST_DIRECTORY_PATTERN =
  /(?:^|\/)(?:__(?:tests?|specs?|mocks?|snapshots?|fixtures?)__|tests?|testdata|cypress|e2e|e2e[-_]tests?|tests?[-_]e2e|playwright|androidtest|integration-tests?|test[-_]?(?:utils?|helpers?|kit|support)|[^/]+\.(?:unit|integration|functional|acceptance|e2e)?tests?)\//i;

// Class-file ecosystems name test classes and test targets in PascalCase, so the two
// case-sensitive rules below apply only to files in these languages.
const CLASS_FILE_EXTENSION_PATTERN = /\.(?:java|kt|kts|cs|php|swift|scala|groovy)$/;

// Case-sensitive PascalCase suffixes for class-file ecosystems (UserServiceTest.java,
// CheckoutSpec.scala, ApiTests.cs). Requires a lowercase letter or digit immediately
// before the suffix so prod classes like ABTest.java (A/B-test models) and words that
// merely end in "test" (latest.php, attest.java) don't match. Deliberate fail-safe:
// acronym-named tests (IOTest.java) are missed here, but those almost always live under
// a test/ directory and are caught by the directory rule above; a false positive would
// silently downgrade prod code to cheap review, which is the worse failure mode.
const CLASS_FILE_TEST_SUFFIX_PATTERN = /[a-z0-9](?:Tests?|Specs?)\.\w+$/;

// Case-sensitive test-target folders: Xcode and .NET targets (CheckoutTests/,
// CheckoutUITests/), with the same lowercase-or-digit guard so A/B-test folders like
// ABTests/ stay prod, and Gradle / Kotlin Multiplatform test source sets
// (src/integrationTest/, src/commonTest/, src/testFixtures/). The class-file scope keeps
// camelCase feature folders in JS projects (src/abTests/) out.
const TEST_TARGET_DIRECTORY_PATTERN =
  /(?:^|\/)(?:[^/]*[a-z0-9](?:UI)?Tests|src\/(?:[a-z][A-Za-z0-9]*Test|testFixtures))\//;

// "spec" names tests in RSpec, Jasmine and Jest (spec/models/user.rb, cart.spec.ts), but
// also specification documents: Spec Kit and Kiro plans (specs/001-checkout/plan.md) and
// API definitions (spec/openapi.yaml, payments.spec.json). Spec suffixes and directories
// count only for code in languages whose test frameworks use spec naming, so those
// documents, and any format not listed, stay visible in the diff and get a full review.
const SPEC_TEST_EXTENSION_PATTERN =
  /\.(?:rb|js|jsx|ts|tsx|mjs|cjs|mts|cts|coffee|php|py|cr|lua|exs|clj|cljs|cljc)$/i;
const SPEC_PATH_PATTERN = /\.specs?\.\w+$|(?:^|\/)specs?\//i;

export function isTestFile(filename: string): boolean {
  return (
    TEST_FILE_NAME_PATTERN.test(filename) ||
    TEST_DIRECTORY_PATTERN.test(filename) ||
    (CLASS_FILE_EXTENSION_PATTERN.test(filename) &&
      (CLASS_FILE_TEST_SUFFIX_PATTERN.test(filename) ||
        TEST_TARGET_DIRECTORY_PATTERN.test(filename))) ||
    (SPEC_TEST_EXTENSION_PATTERN.test(filename) && SPEC_PATH_PATTERN.test(filename))
  );
}
